/**
 * A tank drawn in section, with the floating roof where the measurement put it.
 *
 * This exists because the number on its own does not explain itself. "42%" is a claim; a
 * roof sitting a bit under halfway down a shell, with the sun coming in at the measured
 * elevation and casting the crescent that was actually measured, is the *reason* for the
 * claim. Operators who have never met shadow gauging read the picture immediately and then
 * trust — or usefully distrust — the percentage.
 *
 * Everything drawn here is measured, not decorative:
 *   - the roof height is `fill`,
 *   - `w` (crescent) and `L` (ground shadow) are drawn at ONE shared scale, so
 *     `fill = 1 - w/L` is visible as a ratio of two lengths on screen,
 *   - the sun ray uses the reading's own elevation angle.
 *
 * Two liberties, both deliberate:
 *   - The shell's height-to-diameter ratio is not to scale. A 91 m tank 23 m tall drawn
 *     truthfully is a letterbox in which nothing is legible.
 *   - When a low sun makes `L` longer than the canvas, w and L are scaled together rather
 *     than clipped. Clipping one would make the picture disagree with the number; scaling
 *     both preserves the only thing the picture is asserting.
 */
import { fillColor } from '../../data/tankGauge';

interface TankCrossSectionProps {
  /** 0..1 */
  fill: number;
  /** Sun elevation in degrees; drives the ray angle. Falls back to a plausible 45. */
  sunElevationDeg?: number | null;
  /** Crescent and ground shadow in pixels — used only for their ratio. */
  crescentPx?: number;
  shadowPx?: number;
  /** Drop the sun, the labels and the dimension lines, for use as a small thumbnail. */
  compact?: boolean;
}

const W = 320;
const H = 180;

/** Tank body. Everything else is laid out relative to these. */
const LEFT = 38;
const SHELL_W = 104;
const RIGHT = LEFT + SHELL_W;
const TOP = 40;
const GROUND = 140;
const SHELL_H = GROUND - TOP;
/** Room left of the canvas edge for the shadow to run into. */
const SHADOW_ROOM = W - RIGHT - 12;

export default function TankCrossSection({
  fill, sunElevationDeg, crescentPx, shadowPx, compact = false,
}: TankCrossSectionProps) {
  const clamped = Math.min(Math.max(fill, 0), 1);
  const colour = fillColor(clamped);

  // The roof rides on the liquid: full means flush with the rim.
  const roofY = TOP + (1 - clamped) * SHELL_H;
  const depth = roofY - TOP;

  const elev = sunElevationDeg && sunElevationDeg > 5 && sunElevationDeg < 89
    ? sunElevationDeg : 45;
  const tanE = Math.tan((elev * Math.PI) / 180);

  // L from the geometry; w from the measured ratio when we have it, so the drawing can
  // never disagree with the percentage beside it.
  const rawShadow = SHELL_H / tanE;
  const rawCrescent = (crescentPx && shadowPx && shadowPx > 0)
    ? rawShadow * (crescentPx / shadowPx)
    : depth / tanE;

  // One scale for both, so the ratio survives.
  const scale = rawShadow > SHADOW_ROOM ? SHADOW_ROOM / rawShadow : 1;
  const shadow = rawShadow * scale;
  const crescent = Math.min(rawCrescent * scale, SHELL_W);

  // Keep the sun on the canvas whatever the elevation.
  const rayLen = Math.min(shadow + 14, SHADOW_ROOM);
  const sunX = RIGHT + rayLen;
  const sunY = Math.max(12, GROUND - rayLen * tanE);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="tank-section" role="img"
         aria-label={`Δεξαμενή ${Math.round(clamped * 100)}% γεμάτη, σε τομή`}>
      <defs>
        <linearGradient id="tank-liquid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colour} stopOpacity="0.7" />
          <stop offset="100%" stopColor={colour} stopOpacity="0.3" />
        </linearGradient>
      </defs>

      {/* Ground, drawn as a lit strip. Without it the shadow band below has nothing to be
          darker THAN, on a panel that is already almost black. */}
      <rect x="0" y={GROUND} width={W} height="12" className="tank-section-ground" />
      <rect x={RIGHT} y={GROUND} width={shadow} height="12"
            className="tank-section-shadow" />

      {!compact && (
        <>
          <line x1={sunX} y1={sunY} x2={RIGHT} y2={TOP} className="tank-section-ray" />
          <circle cx={sunX} cy={sunY} r="6" className="tank-section-sun" />
        </>
      )}

      {/* liquid */}
      <rect x={LEFT} y={roofY} width={SHELL_W} height={GROUND - roofY}
            fill="url(#tank-liquid)" />

      {/* the crescent: the sun-facing wall's shadow falling on the roof — the w of the
          ratio, and the whole measurement */}
      {crescent > 0.6 && (
        <rect x={RIGHT - crescent} y={roofY - 1} width={crescent} height="8"
              className="tank-section-crescent" />
      )}

      {/* floating roof */}
      <rect x={LEFT} y={roofY - 3} width={SHELL_W} height="6" rx="1"
            style={{ fill: colour }} className="tank-section-roof" />

      {/* shell, last so it sits over the liquid */}
      <path d={`M ${LEFT} ${TOP} L ${LEFT} ${GROUND} L ${RIGHT} ${GROUND} L ${RIGHT} ${TOP}`}
            className="tank-section-shell" fill="none" />

      {!compact && (
        <>
          {/* L */}
          <line x1={RIGHT} y1={GROUND + 17} x2={RIGHT + shadow} y2={GROUND + 17}
                className="tank-section-measure" />
          <text x={RIGHT + shadow / 2} y={GROUND + 31} className="tank-section-label"
                textAnchor="middle">L</text>
          {/* w */}
          {crescent > 8 && (
            <text x={RIGHT - crescent / 2} y={roofY - 7} className="tank-section-label"
                  textAnchor="middle">w</text>
          )}
          {/* roof depth */}
          {depth > 14 && (
            <>
              <line x1={LEFT - 11} y1={TOP} x2={LEFT - 11} y2={roofY}
                    className="tank-section-measure" />
              <text x={LEFT - 15} y={TOP + depth / 2 + 4}
                    className="tank-section-label" textAnchor="end">d</text>
            </>
          )}
        </>
      )}
    </svg>
  );
}
